import { API_BASE_URL, apiFetch, buildAuthHeaders, getOpenApi, httpBlob, resolveEndpoint } from "./client";
import { listJobs } from "./jobs";

let mockSeq = 1;

function fakeId(prefix) {
  const id = `${prefix}_${String(mockSeq).padStart(4, "0")}`;
  mockSeq += 1;
  return id;
}

function fillPathTemplate(pathTemplate, params) {
  let out = pathTemplate;
  Object.entries(params || {}).forEach(([k, v]) => {
    out = out.replace(new RegExp(`{${k}}`, "g"), encodeURIComponent(String(v)));
  });
  return out;
}

function firstString(...values) {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function firstNonEmptyString(...values) {
  for (const v of values) {
    if (typeof v === "string") {
      const t = v.trim();
      if (t) return t;
    }
  }
  return "";
}

function firstIdFromObject(obj, keys) {
  if (!obj || typeof obj !== "object") return "";
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function snippet(value, max = 220) {
  const text =
    typeof value === "string"
      ? value
      : value && typeof value === "object"
        ? JSON.stringify(value)
        : String(value ?? "");
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max)}...` : clean;
}

function normalizePlatform(value) {
  const p = String(value || "").trim().toLowerCase();
  return p === "symbility" ? "symbility" : "xactimate";
}

function toSnakeCase(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

function normalizePhotoObject(input) {
  const row = input && typeof input === "object" ? input : {};
  const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
  const explicitString = (obj, key) => (obj?.[key] == null ? "" : String(obj[key]));
  const pickExplicitOrFirst = (keys, fallbacks) => {
    for (const key of keys) {
      if (hasOwn(row, key)) return explicitString(row, key);
    }
    return firstNonEmptyString(...fallbacks);
  };
  const analysis =
    row.analysis && typeof row.analysis === "object"
      ? row.analysis
      : row.vision && typeof row.vision === "object"
        ? row.vision
        : {};
  const roomValue = pickExplicitOrFirst(
    ["room", "room_label", "roomLabel"],
    [analysis.room, analysis.room_label, analysis.roomLabel]
  );
  const damageTypeValue = pickExplicitOrFirst(
    ["damage_type", "damageType", "damage_label", "damageLabel"],
    [analysis.damage_type, analysis.damageType, analysis.damage_label, analysis.damageLabel]
  );
  const descriptionValue = pickExplicitOrFirst(
    ["description", "damage_description", "damageDescription"],
    [analysis.description, analysis.damage_description, analysis.damageDescription]
  );
  const confidenceCandidates = [
    row.confidence,
    row.vision_confidence,
    row.vision_confidence_damage,
    row.vision_confidence_room,
    analysis.confidence,
    analysis.vision_confidence,
    analysis.vision_confidence_damage,
    analysis.vision_confidence_room,
  ]
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  const confidence =
    confidenceCandidates.length > 0
      ? Math.max(0, Math.min(1, confidenceCandidates.reduce((a, b) => a + b, 0) / confidenceCandidates.length))
      : null;
  return {
    id: firstString(row.id, row.photo_id, row.photoId),
    filename: firstString(row.filename, row.name),
    content_type: firstString(row.content_type, row.contentType),
    storage_url: firstString(row.storage_url, row.storageUrl, row.url),
    room: roomValue || null,
    damage_type: damageTypeValue || null,
    description: descriptionValue || null,
    confidence,
    analysis_source: firstString(row.analysis_source, row.analysisSource),
    analysis_error: firstString(row.analysis_error, row.analysisError),
    analysis,
    created_at: firstString(row.created_at, row.createdAt),
  };
}

async function sha256HexFromFile(file) {
  if (!file || !window?.crypto?.subtle) return "";
  try {
    const buf = await file.arrayBuffer();
    const hash = await window.crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch (_e) {
    return "";
  }
}

export function buildPlaceholderObservations(photoIds = []) {
  const ids = Array.isArray(photoIds)
    ? photoIds.map((x) => String(x || "").trim()).filter(Boolean)
    : [];

  if (ids.length > 0) {
    return ids.map((photoId) => ({
      photo_id: photoId,
      room: "Unknown",
      description: "Uploaded photo",
      confidence: 0.85,
      needs_review: true,
      meta: {},
    }));
  }

  return [
    {
      photo_id: "p1",
      room: "Unknown",
      description: "Uploaded photo",
      confidence: 0.85,
      needs_review: true,
      meta: {},
    },
  ];
}

async function tryResolvePreferredPath(pathsPreferred, method) {
  const preferred = await resolveEndpoint(pathsPreferred);
  if (preferred) return preferred;

  try {
    const spec = await getOpenApi();
    const paths = (spec && spec.paths) || {};
    const methodLower = String(method || "").toLowerCase();
    const candidates = Object.keys(paths).filter((p) => paths[p] && paths[p][methodLower]);
    for (const p of candidates) {
      if (pathsPreferred.some((x) => x && p.includes(x.replace(/[{}]/g, "")))) return p;
    }
  } catch (e) {
    return "";
  }
  return "";
}

export async function createJob(name) {
  const jobPath = await tryResolvePreferredPath(["/jobs"], "post");
  if (!jobPath) {
    return { jobId: fakeId("job"), mocked: true };
  }

  const normalizedName = String(name || "").trim();
  if (!normalizedName) {
    throw new Error("Job name is required");
  }
  const payload = { name: normalizedName };
  const { data } = await apiFetch(jobPath, { method: "POST", body: payload });
  const jobId = firstString(data?.jobId, data?.id, data?.job_id);
  if (!jobId) {
    throw new Error("Job created but no job id returned");
  }
  return { jobId, mocked: false };
}

export async function updateJobName(jobId, name) {
  const id = String(jobId || "").trim();
  const normalizedName = String(name || "").trim();
  if (!id) throw new Error("Job id is required");
  if (!normalizedName) throw new Error("Job name is required");

  const { data } = await apiFetch(`/jobs/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { name: normalizedName },
  });
  return data;
}

export async function uploadPhotos(jobId, files, { onProgress } = {}) {
  const normalized = Array.from(files || []);
  const uploadPathTemplate = await tryResolvePreferredPath(
    ["/jobs/{job_id}/photos/upload", "/jobs/{job_id}/photos"],
    "post"
  );

  if (!uploadPathTemplate) {
    const fallbackPhotos = normalized.map((file, index) => ({
      id: `photo_${index + 1}`,
      filename: String(file?.name || `photo_${index + 1}`),
      content_type: String(file?.type || ""),
      storage_url: "",
      room: null,
      damage_type: null,
      description: null,
      created_at: "",
    }));
    return { photos: fallbackPhotos, photoIds: fallbackPhotos.map((p) => p.id), mocked: true };
  }

  const basePath = fillPathTemplate(uploadPathTemplate, { job_id: jobId, jobId: jobId, id: jobId });
  const path = `${basePath}${basePath.includes("?") ? "&" : "?"}analyze=false`;
  const form = new FormData();
  normalized.forEach((file) => form.append("files", file));
  let data;
  if (typeof onProgress === "function") {
    data = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = /^https?:\/\//i.test(path)
        ? path
        : `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
      xhr.open("POST", url, true);
      Object.entries(buildAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
        onProgress(pct);
      };
      xhr.onerror = () => reject(new Error("Photo upload failed"));
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;
        const raw = xhr.responseText || "";
        let parsed = null;
        try {
          parsed = raw ? JSON.parse(raw) : null;
        } catch (_e) {
          parsed = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          resolve(parsed || {});
          return;
        }
        const detail = parsed?.detail || raw || "Photo upload failed";
        reject(new Error(`HTTP ${xhr.status}: ${snippet(detail)}`));
      };
      xhr.send(form);
    });
  } else {
    const res = await apiFetch(path, { method: "POST", body: form });
    data = res.data;
  }

  const rawPhotos = Array.isArray(data?.photos)
    ? data.photos
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];
  const photos = rawPhotos.map(normalizePhotoObject).filter((p) => p.id);
  const photoIds = photos.map((p) => p.id);
  return { photos, photoIds, mocked: false };
}

export async function listPhotos(jobId) {
  const pathTemplate = await tryResolvePreferredPath(["/jobs/{job_id}/photos"], "get");
  if (!pathTemplate) {
    return { photos: [], photoIds: [], mocked: true };
  }

  const path = fillPathTemplate(pathTemplate, { job_id: jobId, jobId: jobId, id: jobId });
  const { data } = await apiFetch(path, { method: "GET" });
  const rawPhotos = Array.isArray(data?.photos)
    ? data.photos
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];
  const photos = rawPhotos.map(normalizePhotoObject).filter((p) => p.id);
  return { photos, photoIds: photos.map((p) => p.id), mocked: false };
}

export async function patchPhoto(jobId, photoId, patch) {
  const pathTemplate = await tryResolvePreferredPath(["/jobs/{job_id}/photos/{photo_id}"], "patch");
  if (!pathTemplate) {
    throw new Error("Photo patch endpoint not available in API");
  }
  const path = fillPathTemplate(pathTemplate, {
    job_id: jobId,
    jobId: jobId,
    id: jobId,
    photo_id: photoId,
    photoId: photoId,
  });
  const payload = {};
  ["room", "damage_type", "description"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(patch || {}, key)) {
      payload[key] = patch[key];
    }
  });
  const { data } = await apiFetch(path, { method: "PATCH", body: payload });
  return normalizePhotoObject(data);
}

export async function listJobPhotos(jobId) {
  const discovered = await tryResolvePreferredPath(["/jobs/{job_id}/photos"], "get");
  const pathTemplate = discovered || "/jobs/{job_id}/photos";
  const path = fillPathTemplate(pathTemplate, { job_id: jobId, jobId: jobId, id: jobId });
  const { data } = await apiFetch(path, { method: "GET" });
  const rawPhotos = Array.isArray(data?.photos)
    ? data.photos
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];
  const photos = rawPhotos.map(normalizePhotoObject).filter((p) => p.id);
  return { photos };
}

export async function patchJobPhoto(jobId, photoId, patch) {
  const discovered = await tryResolvePreferredPath(["/jobs/{job_id}/photos/{photo_id}"], "patch");
  const pathTemplate = discovered || "/jobs/{job_id}/photos/{photo_id}";
  const path = fillPathTemplate(pathTemplate, {
    job_id: jobId,
    jobId: jobId,
    id: jobId,
    photo_id: photoId,
    photoId: photoId,
  });
  const payload = {};
  ["room", "damage_type", "description"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(patch || {}, key)) {
      payload[key] = patch[key];
    }
  });
  const { data } = await apiFetch(path, { method: "PATCH", body: payload });
  return normalizePhotoObject(data);
}

export async function deleteJobPhotos(jobId, photoIds) {
  const id = String(jobId || "").trim();
  const ids = Array.isArray(photoIds) ? photoIds.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (!id || ids.length < 1) return { deleted_ids: [], requested: ids.length };
  const discovered = await tryResolvePreferredPath(["/jobs/{job_id}/photos"], "delete");
  const pathTemplate = discovered || "/jobs/{job_id}/photos";
  const path = fillPathTemplate(pathTemplate, { job_id: id, jobId: id, id });
  const { data } = await apiFetch(path, { method: "DELETE", body: { photo_ids: ids } });
  return data;
}

export async function fetchJobPhotoBlobUrl(jobId, photoId, storageUrl = "") {
  const path = firstNonEmptyString(
    storageUrl,
    fillPathTemplate("/jobs/{job_id}/photos/{photo_id}/file", {
      job_id: jobId,
      jobId: jobId,
      photo_id: photoId,
      photoId: photoId,
    })
  );
  const blob = await httpBlob(path, { method: "GET" });
  return URL.createObjectURL(blob);
}

export async function reanalyzeJobPhoto(jobId, photoId) {
  const candidates = [
    "/jobs/{job_id}/photos/{photo_id}/analyze",
    "/photos/{photo_id}/analyze",
  ];

  let pathTemplate = "";
  let usesJobScoped = false;
  for (const c of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const resolved = await tryResolvePreferredPath([c], "post");
    if (resolved) {
      pathTemplate = resolved;
      usesJobScoped = resolved.includes("{job_id}");
      break;
    }
  }

  if (!pathTemplate) {
    pathTemplate = "/jobs/{job_id}/photos/{photo_id}/analyze";
    usesJobScoped = true;
  }

  const path = fillPathTemplate(pathTemplate, {
    job_id: jobId,
    jobId: jobId,
    photo_id: photoId,
    photoId: photoId,
    id: photoId,
  });
  const pathWithQuery = `${path}${path.includes("?") ? "&" : "?"}force=1`;

  const { data } = await apiFetch(pathWithQuery, { method: "POST" });

  // Common response variants from analysis endpoints.
  const room = firstNonEmptyString(data?.room, data?.room_label, data?.roomLabel);
  const damageType = firstNonEmptyString(
    data?.damage_type,
    data?.damage_label,
    data?.damageLabel,
    data?.damage
  );
  const description = firstNonEmptyString(
    data?.description,
    data?.damage_description,
    data?.damageDescription,
    data?.summary
  );

  const confidenceCandidates = [
    data?.confidence,
    data?.confidence_room,
    data?.confidence_damage,
    data?.vision_confidence,
    data?.vision_confidence_damage,
    data?.vision_confidence_room,
  ]
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  const confidence =
    confidenceCandidates.length > 0
      ? Math.max(0, Math.min(1, confidenceCandidates.reduce((a, b) => a + b, 0) / confidenceCandidates.length))
      : null;

  // Persist new labels on the job photo record when we can.
  if (!usesJobScoped && jobId && photoId) {
    try {
      const payload = {};
      if (room) payload.room = room;
      if (damageType) payload.damage_type = damageType;
      if (description) payload.description = description;
      if (Object.keys(payload).length > 0) {
        await patchJobPhoto(jobId, photoId, payload);
      }
    } catch (_e) {
      // Best effort only; caller still receives latest analysis payload.
    }
  }

  return {
    id: String(photoId || ""),
    room: room || null,
    damage_type: damageType || null,
    description: description || null,
    confidence,
    source: firstNonEmptyString(data?.source),
    error: firstNonEmptyString(data?.error, data?.detail),
    raw: data,
  };
}

export async function uploadGuidelines(jobId, file) {
  if (!file) return { guidelineDocumentId: "", mocked: true };
  try {
    const existing = await listGuidelines();
    const docs = Array.isArray(existing?.guidelines) ? existing.guidelines : [];
    const fileHash = await sha256HexFromFile(file);
    const byHash = fileHash
      ? docs.find((d) => String(d.sha256 || "").toLowerCase() === fileHash.toLowerCase())
      : null;
    const byName = docs.find(
      (d) => String(d.filename || "").trim().toLowerCase() === String(file.name || "").trim().toLowerCase()
    );
    const match = byHash || byName;
    if (match?.id) {
      return { guidelineDocumentId: match.id, mocked: false, reused: true };
    }
  } catch (_e) {
    // best-effort duplicate check
  }

  const pathTemplate = await tryResolvePreferredPath(
    ["/jobs/{job_id}/guidelines", "/jobs/{job_id}/documents", "/guidelines/upload", "/documents/upload"],
    "post"
  );
  const resolvedPathTemplate = pathTemplate || "/guidelines/upload";

  const path = fillPathTemplate(resolvedPathTemplate, { job_id: jobId, jobId: jobId, id: jobId });
  const form = new FormData();
  form.append("file", file);
  const { data } = await apiFetch(path, { method: "POST", body: form });
  const guidelineDocumentId = firstIdFromObject(data, [
    "guidelineDocumentId",
    "guideline_document_id",
    "documentId",
    "document_id",
    "id",
  ]);
  return { guidelineDocumentId: guidelineDocumentId || fakeId("guideline"), mocked: false };
}

export async function listGuidelines() {
  const pathTemplate = await tryResolvePreferredPath(["/guidelines"], "get");
  const { data } = await apiFetch(pathTemplate || "/guidelines", { method: "GET" });
  const rows = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
  const guidelines = rows
    .map((row) => ({
      id: firstString(row?.id),
      filename: firstString(row?.filename, row?.name) || "Guideline document",
      created_at: firstString(row?.created_at, row?.createdAt),
      sha256: firstString(row?.sha256),
    }))
    .filter((g) => g.id);
  return { guidelines, mocked: false };
}

export async function deleteGuideline(documentId) {
  const discovered = await tryResolvePreferredPath(["/guidelines/{document_id}"], "delete");
  const pathTemplate = discovered || "/guidelines/{document_id}";
  const path = fillPathTemplate(pathTemplate, {
    document_id: documentId,
    documentId: documentId,
    id: documentId,
  });
  const { data } = await apiFetch(path, { method: "DELETE" });
  return data || { ok: true };
}

export async function attachGuidelineToJob(jobId, { platform = "xactimate", profileId = "default", documentId } = {}) {
  const normalizedPlatform = normalizePlatform(platform);
  const docId = String(documentId || "").trim();
  if (!jobId || !docId) {
    throw new Error("attachGuidelineToJob requires jobId and documentId");
  }
  const discovered = await tryResolvePreferredPath(["/guidelines/jobs/{job_id}/attach"], "post");
  const pathTemplate = discovered || "/guidelines/jobs/{job_id}/attach";
  const path = fillPathTemplate(pathTemplate, {
    job_id: jobId,
    jobId: jobId,
    id: jobId,
  });
  const payload = {
    platform: normalizedPlatform,
    profile_id: String(profileId || "default"),
    document_id: docId,
  };
  const { data } = await apiFetch(path, { method: "POST", body: payload });
  return data || { ok: true };
}

export async function listGuidelineUsage() {
  const jobs = await listJobs({ limit: 200, offset: 0 });
  const usageByDocumentId = {};
  const pathTemplate = await tryResolvePreferredPath(["/guidelines/jobs/{job_id}"], "get");
  const resolved = pathTemplate || "/guidelines/jobs/{job_id}";
  await Promise.all(
    (Array.isArray(jobs) ? jobs : []).map(async (job) => {
      const jobId = String(job?.id || "").trim();
      if (!jobId) return;
      try {
        const path = fillPathTemplate(resolved, { job_id: jobId, jobId, id: jobId });
        const { data } = await apiFetch(path, { method: "GET" });
        const rows = Array.isArray(data) ? data : [];
        rows.forEach((row) => {
          const docId = String(row?.document_id || row?.documentId || "").trim();
          if (!docId) return;
          const when = String(row?.created_at || row?.createdAt || "").trim();
          const prev = usageByDocumentId[docId];
          if (!prev || (when && when > prev.last_used_at)) {
            usageByDocumentId[docId] = {
              document_id: docId,
              job_id: jobId,
              last_used_at: when || prev?.last_used_at || "",
            };
          }
        });
      } catch (_e) {
        // best-effort usage aggregation
      }
    })
  );
  return usageByDocumentId;
}

export async function uploadSketch(jobId, file) {
  if (!file) return { sketchDocumentId: "", mocked: true };

  const pathTemplate = await tryResolvePreferredPath(
    ["/jobs/{job_id}/sketch", "/jobs/{job_id}/sketches", "/sketch/upload", "/documents/upload"],
    "post"
  );
  if (!pathTemplate) {
    return { sketchDocumentId: fakeId("sketch"), mocked: true };
  }

  const path = fillPathTemplate(pathTemplate, { job_id: jobId, jobId: jobId, id: jobId });
  const form = new FormData();
  form.append("file", file);
  const { data } = await apiFetch(path, { method: "POST", body: form });
  const sketchDocumentId = firstIdFromObject(data, [
    "sketchDocumentId",
    "sketch_document_id",
    "documentId",
    "document_id",
    "id",
  ]);
  return { sketchDocumentId: sketchDocumentId || fakeId("sketch"), mocked: false };
}

export async function runBuild(
  jobId,
  {
    platform,
    profileId,
    profile_id,
    observations,
    photoIds,
    photo_ids,
    guidelineDocumentId,
    guideline_document_id,
    sketchDocumentId,
    sketch_document_id,
    ...rest
  } = {}
) {
  const pathTemplate = await tryResolvePreferredPath(
    [
      "/suggestions/jobs/{job_id}/run",
      "/suggestions/jobs/{job_id}/run-from-photos",
      "/jobs/{job_id}/build/run",
      "/build/run",
    ],
    "post"
  );

  if (!pathTemplate) {
    throw new Error("Build run endpoint not available in API");
  }

  const path = fillPathTemplate(pathTemplate, { job_id: jobId, jobId: jobId, id: jobId });
  const resolvedPhotoIds = Array.isArray(photo_ids)
    ? photo_ids
    : Array.isArray(photoIds)
      ? photoIds
      : [];
  const resolvedObservations =
    Array.isArray(observations) && observations.length > 0
      ? observations
      : buildPlaceholderObservations(resolvedPhotoIds);
  const payload = {
    platform: normalizePlatform(platform),
    profile_id: String(profile_id || profileId || "default"),
    observations: resolvedObservations,
  };
  if (guideline_document_id || guidelineDocumentId) {
    payload.guideline_document_id = String(guideline_document_id || guidelineDocumentId);
  }
  if (sketch_document_id || sketchDocumentId) {
    payload.sketch_document_id = String(sketch_document_id || sketchDocumentId);
  }
  Object.entries(rest || {}).forEach(([key, value]) => {
    if (typeof value === "undefined") return;
    const snakeKey = toSnakeCase(key);
    payload[snakeKey] = value;
  });

  const { data } = await apiFetch(path, { method: "POST", body: payload });
  const setId = firstString(data?.setId, data?.set_id, data?.id);
  return { setId: setId || "", raw: data, mocked: false };
}

export async function getBuildStatus(id, { platform = "xactimate" } = {}) {
  const pathTemplate = await tryResolvePreferredPath(
    [
      "/suggestions/jobs/{job_id}/latest",
      "/suggestions/sets/{set_id}",
      "/suggestions/jobs/{job_id}",
      "/jobs/{job_id}/build/status",
      "/jobs/{job_id}",
    ],
    "get"
  );

  if (!pathTemplate) {
    return { supported: false, state: "unknown", progress: 0, message: "No status endpoint found" };
  }

  try {
    let path = fillPathTemplate(pathTemplate, { set_id: id, job_id: id, id });
    if (pathTemplate.includes("/suggestions/jobs/{job_id}/latest")) {
      const query = new URLSearchParams({ platform: platform || "xactimate" }).toString();
      path = `${path}?${query}`;
    }
    const { data } = await apiFetch(path, { method: "GET" });
    const state = firstString(data?.state, data?.status, data?.phase) || "running";
    const progressNum = Number(data?.progress);
    const progress = Number.isFinite(progressNum) ? Math.max(0, Math.min(100, progressNum)) : 0;
    const message = firstString(data?.message, data?.detail) || state;
    return { supported: true, state, progress, message, raw: data };
  } catch (e) {
    const status = Number(e?.status) || 0;
    if (status === 404) {
      return {
        supported: true,
        state: "not_found",
        progress: 0,
        message: "No suggestion set exists yet for this job/platform.",
        raw: null,
      };
    }
    const detail = e?.body ? snippet(e.body) : "";
    const message = `Status request failed${status ? ` (${status})` : ""}: ${detail || e?.message || "Unknown error"}`;
    return { supported: true, state: "error", progress: 0, message };
  }
}

async function checkEstimatorPackReady(
  jobId,
  {
    platform = "xactimate",
    include_all = true,
    include_rationale = true,
    optimize_entry_order = 1,
  } = {}
) {
  const path = fillPathTemplate("/suggestions/jobs/{job_id}/estimator-pack.zip", {
    job_id: jobId,
    jobId,
  });
  const query = new URLSearchParams({
    platform: String(platform || "xactimate"),
    include_all: include_all ? "true" : "false",
    include_rationale: include_rationale ? "true" : "false",
    optimize_entry_order: String(optimize_entry_order),
  }).toString();
  const fullUrl = `${API_BASE_URL}${path}${path.includes("?") ? "&" : "?"}${query}`;

  try {
    const res = await fetch(fullUrl, {
      method: "GET",
      headers: buildAuthHeaders(),
    });
    if (res.ok) {
      return { ready: true, ref: { kind: "job", id: String(jobId) } };
    }
    if (res.status >= 500) {
      const text = await res.text().catch(() => "");
      return { ready: false, transient: true, status: res.status, detail: snippet(text) };
    }
    return { ready: false, status: res.status };
  } catch (e) {
    return {
      ready: false,
      transient: true,
      status: 0,
      detail: snippet(e?.message || "network error"),
    };
  }
}

export async function pollEstimatorPackReady(
  jobId,
  {
    platform = "xactimate",
    include_all = true,
    include_rationale = true,
    optimize_entry_order = 1,
    maxAttempts = 60,
    intervalMs = 1000,
    onProgress,
  } = {}
) {
  let lastTransient = "";
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (typeof onProgress === "function") {
      onProgress({
        phase: "Packaging",
        progress: Math.round((attempt / maxAttempts) * 100),
        message: "Checking estimator pack availability",
        attempt,
        maxAttempts,
      });
    }
    const pack = await checkEstimatorPackReady(jobId, {
      platform,
      include_all,
      include_rationale,
      optimize_entry_order,
    });
    if (pack.ready) return pack;
    if (pack.error) {
      return { ready: false, error: pack.error };
    }
    if (pack.transient) {
      lastTransient = pack.detail || `status ${pack.status || 0}`;
    }
    if (attempt < maxAttempts) {
      await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
    }
  }
  return {
    ready: false,
    error: lastTransient
      ? `Timed out waiting for estimator pack after ${maxAttempts} attempts (last error: ${lastTransient})`
      : `Timed out waiting for estimator pack after ${maxAttempts} attempts`,
  };
}

export async function downloadEstimatorPack(
  ref,
  { platform = "xactimate", includeAll = true, includeRationale = true, optimizeEntryOrder = true } = {}
) {
  const normalizedRef =
    ref && typeof ref === "object" && ref.kind && ref.id
      ? { kind: String(ref.kind).toLowerCase(), id: String(ref.id) }
      : { kind: "job", id: String(ref || "") };
  if (!normalizedRef.id) {
    throw new Error("Estimator pack reference is required");
  }

  const pathTemplate =
    normalizedRef.kind === "set"
      ? "/suggestions/sets/{set_id}/estimator-pack.zip"
      : "/suggestions/jobs/{job_id}/estimator-pack.zip";

  const path = fillPathTemplate(pathTemplate, {
    job_id: normalizedRef.id,
    set_id: normalizedRef.id,
    jobId: normalizedRef.id,
    setId: normalizedRef.id,
    id: normalizedRef.id,
  });
  const url = new URL(`${API_BASE_URL}${path}`);
  url.searchParams.set("platform", platform);
  url.searchParams.set("include_all", includeAll ? "true" : "false");
  url.searchParams.set("include_rationale", includeRationale ? "true" : "false");
  url.searchParams.set("optimize_entry_order", optimizeEntryOrder ? "1" : "0");

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Estimator pack download failed (${res.status}): ${text.slice(0, 200)}`);
  }
  return await res.blob();
}

export async function lookupPublicSafetyContacts({ lossType, zipCode = "", address = "" } = {}) {
  const normalizedLoss = String(lossType || "").trim().toLowerCase();
  if (!normalizedLoss) return { contacts: [], reason: "missing_loss_type" };

  const discovered = await tryResolvePreferredPath(["/public-safety/contacts"], "get");
  const path = discovered || "/public-safety/contacts";
  const params = new URLSearchParams({ loss_type: normalizedLoss });
  const zip = String(zipCode || "").trim();
  const addr = String(address || "").trim();
  if (zip) params.set("zip_code", zip);
  if (addr) params.set("address", addr);

  const { data } = await apiFetch(`${path}?${params.toString()}`, { method: "GET" });
  const rows = Array.isArray(data?.contacts) ? data.contacts : [];
  const contacts = rows.map((row) => ({
    name: firstString(row?.name) || "Agency",
    type: firstString(row?.type),
    phone: firstString(row?.phone),
    address: firstString(row?.address),
    website: firstString(row?.website),
    distance_miles: Number.isFinite(Number(row?.distance_miles)) ? Number(row.distance_miles) : null,
    source: firstString(row?.source),
  }));
  return { contacts, reason: firstString(data?.reason), resolved_location: firstString(data?.resolved_location) };
}
